/**
 * Réglages du compte (design system, EcranReglagesDuCompte, CaptureDeTouche) : thème,
 * touches de révision, couleurs de surlignage personnelles, mot de passe, déconnexion.
 */
import { useEffect, useRef, useState } from 'react';
import {
  TOUCHE_RETOURNER,
  TOUCHES_PAR_DEFAUT,
  type ActionClavier,
  type Touches,
} from '../../clavier/touches';
import { Bouton } from '../composants/Bouton';
import { ChampTexte } from '../composants/ChampTexte';
import { CommandeSegmentee } from '../composants/CommandeSegmentee';
import { Dialogue } from '../composants/Dialogue';
import { Icone } from '../composants/Icone';
import { Menu, type Ancre } from '../composants/Menu';
import { useNotifier } from '../composants/Notifications';
import { GroupeDeReglages, Interrupteur, LigneDeReglage } from '../composants/Reglages';
import { Touche } from '../composants/Touche';
import { Coquille } from '../Coquille';
import { ErreurCompte, LONGUEUR_MOT_DE_PASSE } from '../donnees/authentification';
import { useDonnees } from '../donnees/contexte';
import { messageErreur } from '../donnees/erreurs';
import type { useTheme } from '../donnees/theme';
import { useDispositionClavier } from '../donnees/touches';

const ACTIONS: { action: ActionClavier; libelle: string; aide: string }[] = [
  {
    action: 'a_revoir',
    libelle: '1 (À revoir)',
    aide: 'Au recto : affiche la réponse. Au verso : donne la note 1 (À revoir).',
  },
  {
    action: 'difficile',
    libelle: '2 (Difficile)',
    aide: 'Au recto : affiche la réponse. Au verso : donne la note 2 (Difficile).',
  },
  {
    action: 'bien',
    libelle: '3 (Bien)',
    aide: 'Au recto : affiche la réponse. Au verso : donne la note 3 (Bien).',
  },
  {
    action: 'facile',
    libelle: '4 (Facile)',
    aide: 'Au recto : affiche la réponse. Au verso : donne la note 4 (Facile).',
  },
  { action: 'modifier', libelle: 'Modifier la carte', aide: 'Au verso seulement.' },
  { action: 'annuler', libelle: 'Annuler la dernière réponse', aide: 'Aucune touche par défaut.' },
];

const MODIFICATEURS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Fn', 'OS']);

function CaptureDeTouches({
  touches,
  onChange,
}: {
  touches: Touches;
  onChange: (touches: Touches) => Promise<void>;
}) {
  const caractere = useDispositionClavier();
  const [attente, setAttente] = useState<ActionClavier | null>(null);
  const [conflit, setConflit] = useState<{
    action: ActionClavier;
    code: string;
    avec: ActionClavier;
  } | null>(null);
  const boutons = useRef(new Map<ActionClavier, HTMLButtonElement>());

  useEffect(() => {
    if (attente === null) return;
    const capturer = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setAttente(null);
        boutons.current.get(attente)?.focus();
        return;
      }
      if (MODIFICATEURS.has(e.key)) return;
      if (e.code === TOUCHE_RETOURNER) {
        setConflit(null);
        return;
      }
      const avec = (Object.keys(touches) as ActionClavier[]).find(
        (a) => a !== attente && touches[a] === e.code,
      );
      const action = attente;
      setAttente(null);
      boutons.current.get(action)?.focus();
      if (avec) {
        setConflit({ action, code: e.code, avec });
        return;
      }
      setConflit(null);
      void onChange({ ...touches, [action]: e.code });
    };
    window.addEventListener('keydown', capturer, true);
    return () => window.removeEventListener('keydown', capturer, true);
  }, [attente, touches, onChange]);

  const libelleDe = (a: ActionClavier) => ACTIONS.find((x) => x.action === a)!.libelle;

  return (
    <section
      aria-labelledby="touches-titre"
      className="fc-ligne-de-reglage-groupe fc-capture-de-touche"
    >
      <header className="fc-ligne-de-reglage-groupe__entete">
        <h2 className="fc-ligne-de-reglage-groupe__titre" id="touches-titre">
          Touches de révision
        </h2>
        <p className="fc-ligne-de-reglage-groupe__description">
          Cliquez sur une touche, puis appuyez sur la nouvelle. Échap annule.
        </p>
      </header>
      <div className="fc-ligne-de-reglage-groupe__panneau">
        {ACTIONS.map(({ action, libelle, aide }) => {
          const code = touches[action];
          const affichee = code ? caractere(code) : 'Aucune';
          return (
            <div key={action} className="fc-ligne-de-reglage">
              <div className="fc-ligne-de-reglage__textes">
                <span className="fc-ligne-de-reglage__libelle" id={`touche-${action}-l`}>
                  {libelle}
                </span>
                <p className="fc-ligne-de-reglage__description" id={`touche-${action}-a`}>
                  {aide}
                </p>
                {conflit?.action === action && (
                  <div className="fc-capture-de-touche__conflit fc-ligne-de-reglage__message">
                    <p className="fc-message-de-champ fc-message-de-champ--erreur">
                      <Icone nom="circle-alert" />
                      <span className="fc-message-de-champ__texte">
                        Cette touche sert déjà à {libelleDe(conflit.avec)}.
                      </span>
                    </p>
                    <Bouton
                      taille="petite"
                      onClick={() => {
                        void onChange({
                          ...touches,
                          [conflit.action]: conflit.code,
                          [conflit.avec]: touches[conflit.action],
                        });
                        setConflit(null);
                      }}
                    >
                      Échanger
                    </Bouton>
                  </div>
                )}
              </div>
              <div className="fc-ligne-de-reglage__commande">
                <button
                  ref={(b) => {
                    if (b) boutons.current.set(action, b);
                  }}
                  type="button"
                  className="fc-capture-de-touche__bouton"
                  aria-pressed={attente === action}
                  aria-labelledby={`touche-${action}-l touche-${action}-t`}
                  aria-describedby={`touche-${action}-a`}
                  onClick={() => {
                    setConflit(null);
                    setAttente(attente === action ? null : action);
                  }}
                >
                  <span className="fc-capture-de-touche__attente">
                    Appuyez sur la nouvelle touche…
                  </span>
                  <span className="fc-sr" id={`touche-${action}-t`}>
                    , touche {affichee}
                  </span>
                  {attente === action ? <Touche attente>…</Touche> : <Touche>{affichee}</Touche>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="fc-capture-de-touche__pied">
        <p className="fc-capture-de-touche__note">
          Les touches sont enregistrées par emplacement sur le clavier : elles restent au même
          endroit si vous changez de disposition, et s’affichent avec le caractère gravé sur votre
          clavier.
        </p>
        <Bouton
          variante="discret"
          taille="compacte"
          icone="rotate-ccw"
          onClick={() => {
            setConflit(null);
            void onChange({ ...TOUCHES_PAR_DEFAUT });
          }}
        >
          Rétablir les touches par défaut
        </Bouton>
      </div>
    </section>
  );
}

export function EcranReglagesDuCompte({ theme }: { theme: ReturnType<typeof useTheme> }) {
  const { depot, compte, preferences, rechargerPreferences, services } = useDonnees();
  const notifier = useNotifier();
  const [menuCouleur, setMenuCouleur] = useState<{ couleur: string; ancre: Ancre } | null>(null);
  const [motDePasse, setMotDePasse] = useState<{
    nouveau: string;
    confirmation: string;
    erreur?: string | undefined;
  } | null>(null);
  const saisieCouleur = useRef<HTMLInputElement>(null);
  const [couleurModifiee, setCouleurModifiee] = useState<string | null>(null);
  const couleurs = preferences?.couleursSurlignage ?? [];

  async function enregistrer(modification: Parameters<typeof depot.modifierPreferences>[0]) {
    try {
      await depot.modifierPreferences(modification);
      await rechargerPreferences();
    } catch (e) {
      notifier({ message: messageErreur(e), statut: 'danger' });
    }
  }

  return (
    <Coquille titre="Réglages" colonne="formulaire">
      <div className="fc-ecran-reglages__entete">
        <h2 className="fc-fenetre__titre-ecran">Réglages</h2>
        <p className="fc-ecran-reglages__sous-titre">
          Valables pour tous vos paquets et sur tous vos appareils.
        </p>
      </div>
      <div className="fc-ecran-reglages__panneau">
        <GroupeDeReglages titre="Apparence">
          <LigneDeReglage
            libelle="Thème"
            description="Système suit le réglage de l’ordinateur. Ce choix est gardé sur cet appareil."
            commande={(ids) => (
              <CommandeSegmentee
                etiquettePar={ids.libelleId}
                options={[
                  { valeur: 'clair', libelle: 'Clair', icone: 'sun' },
                  { valeur: 'sombre', libelle: 'Sombre', icone: 'moon' },
                  { valeur: 'systeme', libelle: 'Système', icone: 'monitor' },
                ]}
                valeur={theme.choix}
                onChoisir={theme.setChoix}
              />
            )}
          />
          <LigneDeReglage
            libelle="Réduire la transparence"
            description="Rend le verre opaque, sans flou. À activer ici sur Safari, qui ne transmet pas ce réglage du système. Gardé sur cet appareil."
            commande={(ids) => (
              <Interrupteur
                id={ids.idCommande}
                libelleId={ids.libelleId}
                descriptionId={ids.descriptionId}
                allume={theme.transparenceReduite}
                onChange={theme.setTransparenceReduite}
              />
            )}
          />
        </GroupeDeReglages>

        {preferences && (
          <CaptureDeTouches
            touches={preferences.touches}
            onChange={(touches) => enregistrer({ touches })}
          />
        )}

        <GroupeDeReglages titre="Surlignage">
          <LigneDeReglage
            libelle="Vos couleurs"
            description="Proposées dans l’éditeur après les cinq couleurs de base (violet, bleu, rouge, jaune, vert)."
            commande={(ids) => (
              <ul
                className="fc-ecran-compte__couleurs"
                aria-labelledby={ids.libelleId}
                aria-describedby={ids.descriptionId}
              >
                {couleurs.map((couleur) => (
                  <li key={couleur}>
                    <button
                      type="button"
                      className="fc-selecteur-de-surlignage__pastille"
                      style={{
                        ['--fc-surlignage' as string]: couleur,
                        ['--fc-surlignage-texte' as string]: 'var(--texte)',
                      }}
                      aria-label={`${couleur}, couleur personnelle : modifier ou retirer`}
                      title={couleur}
                      aria-haspopup="menu"
                      aria-expanded={menuCouleur?.couleur === couleur}
                      onClick={(e) => setMenuCouleur({ couleur, ancre: e.currentTarget })}
                    />
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className="fc-selecteur-de-surlignage__ajouter"
                    aria-label="Ajouter une couleur"
                    title="Ajouter une couleur"
                    onClick={() => {
                      setCouleurModifiee(null);
                      saisieCouleur.current?.click();
                    }}
                  >
                    <Icone nom="plus" />
                  </button>
                  <input
                    ref={saisieCouleur}
                    type="color"
                    className="fc-sr"
                    tabIndex={-1}
                    aria-hidden="true"
                    defaultValue="#cdb4db"
                    onChange={(e) => {
                      const nouvelle = e.target.value.toLowerCase();
                      const liste = couleurModifiee
                        ? couleurs.map((c) => (c === couleurModifiee ? nouvelle : c))
                        : [...couleurs.filter((c) => c !== nouvelle), nouvelle];
                      void enregistrer({ couleursSurlignage: [...new Set(liste)].slice(-50) });
                    }}
                  />
                </li>
              </ul>
            )}
          />
        </GroupeDeReglages>

        <GroupeDeReglages titre="Compte">
          <LigneDeReglage
            libelle="Adresse e-mail"
            commande={(ids) => (
              <span className="fc-ecran-compte__valeur" id={ids.idCommande}>
                {compte.email}
              </span>
            )}
          />
          <LigneDeReglage
            libelle="Mot de passe"
            commande={(ids) => (
              <Bouton
                taille="compacte"
                id={ids.idCommande}
                onClick={() => setMotDePasse({ nouveau: '', confirmation: '' })}
              >
                Changer le mot de passe
              </Bouton>
            )}
          />
          <LigneDeReglage
            libelle="Se déconnecter"
            description="Sur cet ordinateur. Vos cartes restent dans votre compte."
            commande={(ids) => (
              <Bouton
                taille="compacte"
                icone="log-out"
                id={ids.idCommande}
                aria-describedby={ids.descriptionId}
                onClick={() => void services.auth.deconnexion()}
              >
                Se déconnecter
              </Bouton>
            )}
          />
        </GroupeDeReglages>
      </div>

      {menuCouleur && (
        <Menu
          ancre={menuCouleur.ancre}
          etiquette={`Couleur ${menuCouleur.couleur}`}
          elements={[
            {
              type: 'action',
              libelle: 'Modifier',
              icone: 'pipette',
              onChoisir: () => {
                setCouleurModifiee(menuCouleur.couleur);
                if (saisieCouleur.current) saisieCouleur.current.value = menuCouleur.couleur;
                saisieCouleur.current?.click();
              },
            },
            { type: 'separateur' },
            {
              type: 'action',
              libelle: 'Retirer',
              icone: 'trash-2',
              danger: true,
              onChoisir: () =>
                void enregistrer({
                  couleursSurlignage: couleurs.filter((c) => c !== menuCouleur.couleur),
                }),
            },
          ]}
          onFermer={() => setMenuCouleur(null)}
        />
      )}

      {motDePasse && (
        <Dialogue
          titre="Changer le mot de passe"
          texte="Il remplacera l’ancien sur tous vos appareils."
          actionPrincipale={{
            libelle: 'Changer le mot de passe',
            onAction: async () => {
              if (motDePasse.nouveau.length < LONGUEUR_MOT_DE_PASSE) {
                setMotDePasse({
                  ...motDePasse,
                  erreur: `Saisissez au moins ${LONGUEUR_MOT_DE_PASSE} caractères.`,
                });
                return;
              }
              if (motDePasse.nouveau !== motDePasse.confirmation) {
                setMotDePasse({
                  ...motDePasse,
                  erreur: 'Les deux mots de passe ne correspondent pas.',
                });
                return;
              }
              try {
                await services.auth.changerMotDePasse(motDePasse.nouveau);
                setMotDePasse(null);
                notifier({ message: 'Mot de passe changé.', statut: 'succes' });
              } catch (e) {
                setMotDePasse({
                  ...motDePasse,
                  erreur: e instanceof ErreurCompte ? e.message : messageErreur(e),
                });
              }
            },
          }}
          onFermer={() => setMotDePasse(null)}
        >
          <ChampTexte
            libelle="Nouveau mot de passe"
            type="mot-de-passe"
            saisieAuto="new-password"
            valeur={motDePasse.nouveau}
            onChange={(v) => setMotDePasse({ ...motDePasse, nouveau: v, erreur: undefined })}
            aide={`Au moins ${LONGUEUR_MOT_DE_PASSE} caractères.`}
          />
          <ChampTexte
            libelle="Confirmer le mot de passe"
            type="mot-de-passe"
            saisieAuto="new-password"
            valeur={motDePasse.confirmation}
            onChange={(v) => setMotDePasse({ ...motDePasse, confirmation: v, erreur: undefined })}
            {...(motDePasse.erreur ? { erreur: motDePasse.erreur } : {})}
          />
        </Dialogue>
      )}
    </Coquille>
  );
}
